import { useCallback, useEffect, useRef, useState } from 'react';
import { getContainer } from '@framework/di/getContainer';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getErrorMessage } from '@framework/message/Mapper/getMessage';
import AskAiPromptsRepository from '@/creditflowresearch/infrastructure/repository/AskAiPromptsRepository';
import { IAskAiPromptsVersion } from '@/creditflowresearch/domain/vo/AskAiPrompts';
import { isSavedVersion } from '../helpers/promptsEntries';

interface IParams {
  isEnabled: boolean,
}

const useAskAiPromptsHistory = ({ isEnabled }: IParams) => {
  const [versions, setVersions] = useState<IAskAiPromptsVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const repository = getContainer().get<AskAiPromptsRepository>(AskAiPromptsRepository);

  const refresh = useCallback(async (): Promise<void> => {
    const requestId = ++requestIdRef.current;
    const isLatest = (): boolean => requestId === requestIdRef.current;

    setIsLoading(true);

    try {
      const items = await repository.getPrompts();

      if (isLatest()) setVersions(items.filter(isSavedVersion));
    } catch (error) {
      if (isLatest()) AlertManager.error(getErrorMessage(error));
    } finally {
      if (isLatest()) setIsLoading(false);
    }
  }, [repository]);

  const remove = async (id: string): Promise<boolean> => {
    try {
      await repository.deleteVersion(id);
      setVersions((prev) => prev.filter((version) => version.id !== id));

      return true;
    } catch (error) {
      AlertManager.error(getErrorMessage(error));

      return false;
    }
  };

  useEffect(() => {
    if (isEnabled) refresh();
  }, [isEnabled, refresh]);

  return { versions, isLoading, refresh, remove };
};

export default useAskAiPromptsHistory;
